import React, { useContext, useEffect, useState } from 'react'
import _ from 'lodash'
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Input, Spin, App, Upload } from "antd"
import { DatePicker } from "antd-jalali"
import dayjs, { Dayjs } from 'dayjs'
import { FaPlus, FaInbox } from "react-icons/fa"
import { API, Enum, Regex } from "../../../resources/constants"
import language from "../../../resources/languages/fa_IR"

// Contexts
import GlobalContext from "../../../contexts/global";
import AxiosContext from "../../../contexts/axios";

// Interfaces
import { AxiosError } from "../../../interfaces/axios"
import type { UploadFile } from 'antd/es/upload/interface';

const { Pages: { HostManagement: { Host: HostLanguage } }, Enum: EnumLanguage, Share, Validation, Message: MessageLanguage } = language
const { HOST_MANAGEMENT_HOST_ID, HOST_MANAGEMENT_HOST_CREATE, HOST_MANAGEMENT_HOST_UPDATE, HOST_MANAGEMENT_HOST_UPLOAD_FILE, HOST_MANAGEMENT_HOST_REMOVE_UPLOAD_FILE, FILE_MANAGEMENT_FILE_DOWNLOAD } = API
const { Message, HostFile } = Enum
const { isHost } = Regex

interface FileType {
    id: string,
    originalName: string,
    fileName: string,
    mimeType: string,
    size: number,
}

interface HostType {
    id: string,
    title: string,
    description: string,
    host: string,
    smsTemplate: object,
    favicon: FileType,
    verticalLogo: FileType,
    horizontalLogo: FileType,
    publish: boolean,
    createdAt: Date,
    updatedAt: Date,
}

export default function CURecord() {

    const [form] = Form.useForm()
    const [, forceUpdate] = useState({})
    const navigate = useNavigate()
    const { accessToken, baseURL } = useContext(GlobalContext)
    const { axios } = useContext(AxiosContext)
    const [loadingRecord, setLoadingRecord] = useState<boolean>(true)
    const [fileListFavicon, setFileListFavicon] = useState<UploadFile[]>([])
    const [fileListVerticalLogo, setFileListVerticalLogo] = useState<UploadFile[]>([])
    const [fileListHorizontalLogo, setFileListHorizontalLogo] = useState<UploadFile[]>([])
    const { id: hostId } = useParams()
    const { message } = App.useApp()

    useEffect(() => {
        // To disable submit button at the beginning.
        forceUpdate({})
        initialAPI().then(response => response)
    }, [])

    const initialAPI = async () => {
        await getRecord()
    }

    const getRecord = async () => hostId ? axios.get(HOST_MANAGEMENT_HOST_ID + hostId).then((response: { data: HostType }) => {
        const { data: host } = response
        form.setFieldsValue({ ...host, smsTemplate: JSON.stringify(host.smsTemplate, null, 2) })
        setFileListFavicon(host?.favicon ? [{
            uid: host?.favicon?.id,
            name: host?.favicon?.originalName,
            status: 'done',
            url: baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + host?.favicon?.id + `?token=${accessToken}`,
        }] : [])
        setFileListVerticalLogo(host?.verticalLogo ? [{
            uid: host?.verticalLogo?.id,
            name: host?.verticalLogo?.originalName,
            status: 'done',
            url: baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + host?.verticalLogo?.id + `?width=1000&height=1000&token=${accessToken}`,
        }] : [])
        setFileListHorizontalLogo(host?.horizontalLogo ? [{
            uid: host?.horizontalLogo?.id,
            name: host?.horizontalLogo?.originalName,
            status: 'done',
            url: baseURL + FILE_MANAGEMENT_FILE_DOWNLOAD + host?.horizontalLogo?.id + `?width=1000&height=1000&token=${accessToken}`,
        }] : [])
        setLoadingRecord(false)
    }).catch((error: AxiosError) => {
        const { response: { data } } = error;
        // @ts-ignore
        message.error(MessageLanguage[data.message]).then(response => response )
    }) : setLoadingRecord(false)

    const onRemove = (file: UploadFile) => {
        const { uid: fileId } = file
        axios.delete(HOST_MANAGEMENT_HOST_REMOVE_UPLOAD_FILE + fileId + '/' + hostId).then((response: {}) =>{
            setFileListFavicon(fileListFavicon.filter(({ uid }) => uid !== fileId))
            setFileListVerticalLogo(fileListVerticalLogo.filter(({ uid }) => uid !== fileId))
            setFileListHorizontalLogo(fileListHorizontalLogo.filter(({ uid }) => uid !== fileId))
            message.success(MessageLanguage[Message.DELETE_FILE]).then(response => response)
        }).catch((error: AxiosError) => {
            const { response: { data } } = error;
            // @ts-ignore
            message.error(MessageLanguage[data.message]).then(response => response )
        })
    }

    const onChange = async (info: { file: UploadFile, fileList: UploadFile[] }, field: string) => {
        const { file, fileList } = info
        const { uid, status } = file;
        switch (field) {
            case HostFile.Favicon:
                setFileListFavicon(fileList);
                break;
            case HostFile.VerticalLogo:
                setFileListVerticalLogo(fileList);
                break;
            case HostFile.HorizontalLogo:
                setFileListHorizontalLogo(fileList);
                break;
        }
        if (status === 'done') {
            await getRecord()
            message.success(MessageLanguage[Message.UPLOAD_SUCCESS]).then(response => response)
        } else if (status === 'error') {
            switch (field) {
                case HostFile.Favicon:
                    setFileListFavicon(fileList.filter(file => (file.uid !== uid)));
                    break;
                case HostFile.VerticalLogo:
                    setFileListVerticalLogo(fileList.filter(file => (file.uid !== uid)));
                    break;
                case HostFile.HorizontalLogo:
                    setFileListHorizontalLogo(fileList.filter(file => (file.uid !== uid)));
                    break;
            }
            const { response } = file;
            // @ts-ignore
            message.error(MessageLanguage[response.message ? response.message : Message.UPLOAD_ERROR]).then(response => response)
        }
    }

    const progress: any = () => ({
        strokeColor: {
            '0%': '#108ee9',
            '100%': '#87d068',
        },
        strokeWidth: 3,
        format: (percent: number) => percent && `${parseFloat(percent.toFixed())}%`,
    })

    const onFinish = (host: { smsTemplate: string } ) => {
        setLoadingRecord(true)
        if (hostId) {
            axios.patch(HOST_MANAGEMENT_HOST_UPDATE + hostId, { ...host, smsTemplate: JSON.parse(host.smsTemplate) }).then((response: {}) => {
                message.success(MessageLanguage[Message.UPDATE_RECORD]).then(response => response)
                setLoadingRecord(false)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        } else {
            axios.post(HOST_MANAGEMENT_HOST_CREATE, { ...host, smsTemplate: JSON.parse(host.smsTemplate) }).then((response: { data: { id: number } }) => {
                message.success(MessageLanguage[Message.CREATE_RECORD]).then(response => response)
                const { data: { id } } = response
                navigate('/host-management/host/edit/' + id)
                setLoadingRecord(false)
            }).catch((error: AxiosError) => {
                const { response: { data } } = error;
                // @ts-ignore
                message.error(MessageLanguage[data.message]).then(response => response)
                setLoadingRecord(false)
            })
        }
    }

    return (
        <Spin spinning={loadingRecord} tip={Share.loading} size="large">
            <Form form={form} name="cu-record" onFinish={onFinish} style={{ maxWidth: 450 }}>
                <Form.Item
                    label={HostLanguage.title}
                    name="title"
                    hasFeedback
                    rules={[
                        { required: true, message: `${HostLanguage.title} ${Validation.required}` },
                    ]}
                >
                    <Input minLength={3} />
                </Form.Item>
                <Form.Item
                  label={HostLanguage.description}
                  name="description"
                  rules={[
                      { required: true, message: `${HostLanguage.description} ${Validation.required}` },
                  ]}
                >
                    <Input.TextArea rows={4} placeholder={HostLanguage.description} />
                </Form.Item>
                <Form.Item
                    label={HostLanguage.host}
                    name="host"
                    hasFeedback
                    rules={[
                        { required: true, message: `${HostLanguage.host} ${Validation.required}` },
                        { pattern: isHost, message: `${HostLanguage.host} ${Validation.pattern}` },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                  label={HostLanguage.smsTemplate}
                  name="smsTemplate"
                  rules={[
                      { required: true, message: `${HostLanguage.smsTemplate} ${Validation.required}` },
                      {
                          validator: (object, value) => {
                              try {
                                  const parsed = JSON.parse(value);
                                  if (!_.isArray(parsed)) return Promise.resolve();
                                  else return Promise.reject(new Error(`${HostLanguage.smsTemplate} ${Validation.pattern}`));
                              } catch (error) {
                                  return Promise.reject(new Error(`${HostLanguage.smsTemplate} ${Validation.pattern}`));
                              }
                          }
                      },
                  ]}
                >
                    <Input.TextArea dir={"ltr"} rows={6} placeholder={HostLanguage.smsTemplate} />
                </Form.Item>
                {
                    hostId && (
                        <>
                            <Form.Item
                              name={HostFile.Favicon}
                            >
                                <Upload.Dragger
                                  name="file"
                                  multiple={false}
                                  fileList={fileListFavicon}
                                  action={baseURL + HOST_MANAGEMENT_HOST_UPLOAD_FILE + hostId + '/' + HostFile.Favicon}
                                  headers={{ Authorization: `Bearer ${accessToken}` }}
                                  onRemove={(file) => onRemove(file)}
                                  onChange={(info) => onChange(info, HostFile.Favicon)}
                                  progress={progress}
                                >
                                    <p className="ant-upload-drag-icon"><FaInbox size={50} /></p>
                                    <p className="ant-upload-text">{ HostLanguage.attachmentFavicon }</p>
                                    <p className="ant-upload-hint">{ Share.attachmentDescription }</p>
                                </Upload.Dragger>
                            </Form.Item>
                            <Form.Item
                              name={HostFile.VerticalLogo}
                            >
                                <Upload.Dragger
                                  name="file"
                                  multiple={false}
                                  fileList={fileListVerticalLogo}
                                  action={baseURL + HOST_MANAGEMENT_HOST_UPLOAD_FILE + hostId + '/' + HostFile.VerticalLogo}
                                  headers={{ Authorization: `Bearer ${accessToken}` }}
                                  onRemove={(file) => onRemove(file)}
                                  onChange={(info) => onChange(info, HostFile.VerticalLogo)}
                                  progress={progress}
                                >
                                    <p className="ant-upload-drag-icon"><FaInbox size={50} /></p>
                                    <p className="ant-upload-text">{ HostLanguage.attachmentVerticalLogo }</p>
                                    <p className="ant-upload-hint">{ Share.attachmentDescription }</p>
                                </Upload.Dragger>
                            </Form.Item>
                            <Form.Item
                              name={HostFile.HorizontalLogo}
                            >
                                <Upload.Dragger
                                  name="file"
                                  multiple={false}
                                  fileList={fileListHorizontalLogo}
                                  action={baseURL + HOST_MANAGEMENT_HOST_UPLOAD_FILE + hostId + '/' + HostFile.HorizontalLogo}
                                  headers={{ Authorization: `Bearer ${accessToken}` }}
                                  onRemove={(file) => onRemove(file)}
                                  onChange={(info) => onChange(info, HostFile.HorizontalLogo)}
                                  progress={progress}
                                >
                                    <p className="ant-upload-drag-icon"><FaInbox size={50} /></p>
                                    <p className="ant-upload-text">{ HostLanguage.attachmentHorizontalLogo }</p>
                                    <p className="ant-upload-hint">{ Share.attachmentDescription }</p>
                                </Upload.Dragger>
                            </Form.Item>
                        </>
                    )
                }
            <Form.Item shouldUpdate>
                {() => (
                    <Button
                        icon={<FaPlus />}
                        type="primary"
                        htmlType="submit"
                        block
                        disabled={
                            !form.getFieldValue('title') ||
                            !form.getFieldValue('description') ||
                            !!form.getFieldsError().filter(({ errors }) => errors.length).length
                    }
                    >
                        { Share.save }
                    </Button>
                )}
            </Form.Item>
        </Form>

        </Spin>
    )
}
